import { Box, Flex } from '@/components';
import { useAnalysisPageSearchQueryStates } from '@/features/analysis/_hooks/search-query-states';
import type { ProjectAnalysisModels } from '@/features/analysis/_models/project';
import {
  checkChildrenRecursively,
  getGraphOptions,
  getLineGraphOptions,
  getSeriesData,
} from '@/features/analysis/_utils';
import { useProjectsSearchFormProviderState } from '@/features/search/_components/projects/_common/ProjectsSearchAccordionProvider';
import { useLocaledAmount } from '@/providers';
import { BigCalculator } from '@/utils';
import type { SortOrder } from 'antd/es/table/interface';
import Highcharts from 'highcharts';
import {
  HighchartsReact,
  type HighchartsReactRefObject,
} from 'highcharts-react-official';
import TreemapModule from 'highcharts/modules/treemap';
import groupBy from 'lodash-es/groupBy';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import type { z } from 'zod';
import { CustomLegend } from './CustomLegend';
import { GraphYearSelectorTabs } from './GraphYearSelectorTabs';

TreemapModule(Highcharts);

type Props = {
  graphData: z.infer<typeof ProjectAnalysisModels>;
  handleGraphInteraction: (ids: string[]) => Promise<void>;
  options:
    | {
        ids: string[];
        names: string[];
      }
    | undefined;
};

export const VisualizerContent: FC<Props> = ({
  graphData,
  handleGraphInteraction,
  options,
}) => {
  const { unit } = useLocaledAmount();
  const {
    searchQueryStates: { order, graphType, analysisAxis },
  } = useAnalysisPageSearchQueryStates();
  const {
    cascaderTargetIds: {
      organizationIds: cascaderOrganizationIds,
      policyIds: cascaderPolicyIds,
    },
    data: {
      organizationsOptions: allOrganizationsOptions,
      policiesOptions: allPoliciesOptions,
    },
  } = useProjectsSearchFormProviderState();

  const highchartsRef = useRef<HighchartsReactRefObject>(null);
  const fiscalYears = graphData.map((item) => item.fiscalYear);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const handlePointClick = useCallback(
    ({ id }: { id: string }) => {
      handleGraphInteraction([...(options?.ids ?? []), id]);
    },
    [handleGraphInteraction, options],
  );

  const groupDataByAnalysisAxisId = groupBy(
    graphData,
    // 分析の軸がnullの場合は年度ごとに表示するため
    analysisAxis === 'year' ? 'analysisAxis.name' : 'analysisAxis.id',
  );
  const isPieOrTreeMapGraph = ['pieChart', 'treeMap'].includes(graphType);

  const sortedSeriesData = useMemo(() => {
    const seriesData = getSeriesData({
      data: groupDataByAnalysisAxisId,
      handlePointClick,
    });

    const orderingYear = order?.split('_')[1];
    if (!order || !orderingYear) {
      return seriesData;
    }

    if (order.includes('ascend' satisfies SortOrder)) {
      return seriesData.sort((a, b) => {
        const valueA =
          a.data.find((item) => item.x === Number(orderingYear))?.y ?? 0;
        const valueB =
          b.data.find((item) => item.x === Number(orderingYear))?.y ?? 0;
        return BigCalculator.subtract(valueA, valueB);
      });
    }

    return seriesData.sort((a, b) => {
      const valueA =
        a.data.find((item) => item.x === Number(orderingYear))?.y ?? 0;
      const valueB =
        b.data.find((item) => item.x === Number(orderingYear))?.y ?? 0;
      return BigCalculator.subtract(valueB, valueA);
    });
  }, [order, groupDataByAnalysisAxisId, handlePointClick]);

  const filteredSeriesData = useMemo(() => {
    if (isPieOrTreeMapGraph && selectedYear) {
      return sortedSeriesData
        .map((item) => {
          return {
            ...item,
            data: item.data.filter((d) => d.x === selectedYear),
          };
        })
        .filter((s) => s.data.length);
    }
    return sortedSeriesData;
  }, [isPieOrTreeMapGraph, selectedYear, sortedSeriesData]);

  const minYear = selectedYear ?? Math.min(...fiscalYears);

  return (
    <Flex gap={8} align="center">
      <CustomLegend
        highchartsRef={highchartsRef}
        legendData={filteredSeriesData.map(({ name, color, custom }) => {
          const { id = null, parentId } = custom;

          const currentId = parentId ?? id ?? '';

          // 分析軸に応じて適切なcascaderデータとオプションを使用
          const isPolicy = analysisAxis === 'policy';
          const targetCascaderIds = isPolicy
            ? cascaderPolicyIds
            : cascaderOrganizationIds;
          const allOptionsData = isPolicy
            ? allPoliciesOptions
            : allOrganizationsOptions;

          const targetNestedIds = targetCascaderIds
            ?.filter((ids) => ids.includes(currentId))
            .at(0);

          return {
            name: name.toString(),
            color,
            id,
            enableDrillDown: checkChildrenRecursively(
              targetNestedIds ?? [currentId],
              allOptionsData ?? [],
            ),
            onClick: () => {
              if (id) {
                handlePointClick({ id });
              }
            },
          };
        })}
      />
      <Box width="80%">
        {isPieOrTreeMapGraph && (
          <Flex align="center" justify="center">
            <GraphYearSelectorTabs
              selectedYear={minYear}
              fiscalYears={fiscalYears}
              onSelect={setSelectedYear}
            />
          </Flex>
        )}
        <HighchartsReact
          ref={highchartsRef}
          highcharts={Highcharts}
          options={getLineGraphOptions({
            graphData,
            graphOptions: getGraphOptions({
              graphType,
              seriesData: sortedSeriesData,
              data: groupDataByAnalysisAxisId,
              selectedYear: minYear,
              unit,
              handlePointClick,
            }),
            graphType,
            unit,
          })}
        />
      </Box>
    </Flex>
  );
};
